import React, { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";
import NavbarMob from "./NavbarMob";
import Navbar from "./Navbar";
import Footer from "./Footer";
import Navbar2 from "./Navbar2";
import Navbar2Mob from "./Navbar2Mob";

const DeleteProfiles = () => {
    const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
    const [employees, setEmployees] = useState([]);
    const [employeeSearch, setEmployeeSearch] = useState("");
    const [blockedProfiles, setBlockedProfiles] = useState([]);
    const [loading, setLoading] = useState(true);

    // Delete Modal States
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletePassword, setDeletePassword] = useState("");
    const [employeeToDelete, setEmployeeToDelete] = useState(null);
    const [passwordError, setPasswordError] = useState("");

    const navigate = useNavigate();
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const customerType = sessionStorage.getItem("customerType");
    const companyName = sessionStorage.getItem("customerName");
    const hasRiyaHubAccess = (companyName || "").trim().toUpperCase() === "RIYA HUB";
    const isAuthorized = customerType === "admin" || customerType === "mainAdmin" || hasRiyaHubAccess;
    const passwordAccess = customerType === "mainAdmin" || hasRiyaHubAccess;

    // Secure Admin Password
    const DELETE_ADMIN_PASSWORD = process.env.REACT_APP_DELETE_ADMIN_PASSWORD;

    useEffect(() => {
        if (!isAuthorized) {
            navigate("/login", { replace: true });
        }
    }, [isAuthorized, navigate]);

    // Fetch Employees
    // Fetch Employees – newest first, missing/invalid dates at bottom
    useEffect(() => {
        if (!isAuthorized) {
            setLoading(false);
            return;
        }

        const fetchEmployees = async () => {
            try {
                const response = await fetch(`${apiBaseUrl}/getAllEmployees`);
                const data = await response.json();

                const sortedEmployees = (data || []).sort((a, b) => {
                    const dateA = getCreationDate(a);
                    const dateB = getCreationDate(b);

                    // Both missing/invalid → keep original order
                    if (!dateA && !dateB) return 0;

                    // Missing/invalid → push to end
                    if (!dateA) return 1;
                    if (!dateB) return -1;

                    // Newest first (descending)
                    return dateB - dateA;
                });

                setEmployees(sortedEmployees);
            } catch (error) {
                console.error("Error fetching employees:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchEmployees();
    }, [apiBaseUrl, isAuthorized]);

    // Helper to safely parse creation date
    const getCreationDate = (item) => {
        if (!item?.created_at) return null;
        const date = new Date(item.created_at);
        return isNaN(date.getTime()) ? null : date;
    };

    // Helper for nice display (used in JSX)
    const getJoinedTimeDisplay = (item) => {
        const date = getCreationDate(item);
        return date
            ? date.toLocaleDateString("en-GB") // → 22/01/2026
            : "Date not available";
    };

    // Fetch Blocked Profiles
    useEffect(() => {
        if (!isAuthorized) return;

        const fetchBlockedProfiles = async () => {
            try {
                const response = await fetch(`${apiBaseUrl}/getBlockedProfiles`);
                const data = await response.json();
                const normalized = (data || []).map((profile) => ({
                    blockedId: profile.BlockedId,
                    profileType: profile.ProfileType,
                    mobileNumber: profile.MobileNumber,
                }));
                setBlockedProfiles(normalized);
            } catch (error) {
                console.error("Error fetching blocked profiles:", error);
            }
        };
        fetchBlockedProfiles();
    }, [apiBaseUrl, isAuthorized]);

    // Open Delete Modal
    const openDeleteModal = (employeeId) => {
        setEmployeeToDelete(employeeId);
        setDeletePassword("");
        setPasswordError("");
        setShowDeleteModal(true);
    };

    // Confirm Delete with Password
    const confirmDeleteWithPassword = async () => {
        if (deletePassword !== DELETE_ADMIN_PASSWORD) {
            setPasswordError("Incorrect password. Deletion denied.");
            return;
        }

        if (!employeeToDelete) return;

        try {
            // Step 1: Delete all job posts
            const jobResponse = await fetch(`${apiBaseUrl}/jobposts/${employeeToDelete}`, {
                method: "DELETE",
            });
            const jobData = await jobResponse.json();

            if (!jobData.success) {
                setPasswordError("Failed to delete job posts: " + (jobData.message || "Unknown error"));
                return;
            }

            // Step 2: Delete employee profile
            const empResponse = await fetch(`${apiBaseUrl}/employee/delete/${employeeToDelete}`, {
                method: "DELETE",
            });
            const empData = await empResponse.json();

            if (empData.success) {
                setEmployees((prev) => prev.filter((e) => e.id !== employeeToDelete));
                closeDeleteModal();
                alert("Employee and all job posts deleted successfully.");
            } else {
                setPasswordError("Failed to delete employee: " + (empData.message || "Unknown error"));
            }
        } catch (error) {
            console.error("Error during deletion:", error);
            setPasswordError("Network error. Please try again.");
        }
    };

    // Close Modal
    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setEmployeeToDelete(null);
        setDeletePassword("");
        setPasswordError("");
    };

    // Block Profile
    const handleBlockProfile = async (blockedId, mobileNumber, profileType) => {
        const confirmBlock = window.confirm("Are you sure you want to block this profile?");
        if (!confirmBlock) return;

        try {
            const response = await fetch(`${apiBaseUrl}/blockProfile`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ blockedId, profileType, mobileNumber }),
            });
            const data = await response.json();

            if (data.success) {
                setBlockedProfiles((prev) => [...prev, { blockedId, profileType, mobileNumber }]);
                alert("Profile blocked successfully.");
            }
        } catch (error) {
            console.error("Error blocking profile:", error);
        }
    };

    // Unblock Profile
    const handleUnblockProfile = async (blockedId, mobileNumber) => {
        const confirmUnblock = window.confirm("Are you sure you want to unblock this profile?");
        if (!confirmUnblock) return;

        try {
            const response = await fetch(`${apiBaseUrl}/unblockProfile`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ blockedId, mobileNumber }),
            });
            const data = await response.json();

            if (data.success) {
                setBlockedProfiles((prev) =>
                    prev.filter((p) => !(p.blockedId === blockedId && p.mobileNumber === mobileNumber)),
                );
                alert("Profile unblocked successfully.");
            }
        } catch (error) {
            console.error("Error unblocking profile:", error);
        }
    };

    // Check if blocked
    const isBlocked = (id, mobile) => blockedProfiles.some((p) => p.blockedId === id && p.mobileNumber === mobile);

    // Filter Employees
    const filteredEmployees = employees.filter((employee) => {
        const rawSearch = employeeSearch.trim().toLowerCase();
        if (!rawSearch) return true;

        const normalizePhone = (num) =>
            (num || "")
                .replace(/^\+91|\+91|\+/g, "")
                .replace(/\s/g, "")
                .replace(/\D/g, "");

        const cleanedSearch = normalizePhone(rawSearch);
        const cleanedEmployeePhone = normalizePhone(employee.mobile_number);

        const phoneMatch = cleanedSearch
            ? cleanedEmployeePhone.includes(cleanedSearch)
            : (employee.mobile_number || "").toString().includes(rawSearch);

        return (
            (employee.company_name || "").toLowerCase().includes(rawSearch) ||
            (employee.id || "").toString().includes(rawSearch) ||
            phoneMatch ||
            (employee.email || "").toLowerCase().includes(rawSearch)
        );
    });

    if (!isAuthorized) {
        return null;
    }

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            {/* Navigation */}
            {isMobile ? <NavbarMob /> : <Navbar />}
            <div className="md:flex hidden">
                <Navbar2 />
            </div>
            <div className="md:hidden flex flex-col">
                <Navbar2Mob />
            </div>

            {/* Main Content */}
            <div className="flex flex-col lg:px-12 px-3 py-10 space-y-8 flex-1">
                <div className="bg-[#d4d4d4] shadow-md rounded-lg p-6">
                    <h2 className="text-3xl font-semibold text-center mb-6 font-display text-gray-800">Manage Employers</h2>

                    {/* Search */}
                    <input
                        type="text"
                        placeholder="Search by Company Name, ID, Mobile, or Email..."
                        value={employeeSearch}
                        onChange={(e) => setEmployeeSearch(e.target.value)}
                        className="mb-6 w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                    />

                    {/* Loading / Empty */}
                    {loading ? (
                        <div className="text-center py-10">
                            <p className="text-gray-600">Loading employers...</p>
                        </div>
                    ) : filteredEmployees.length === 0 ? (
                        <div className="text-center py-10">
                            <p className="text-gray-600">No employers found.</p>
                        </div>
                    ) : (
                        /* Employers Grid */
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {filteredEmployees.map((employee) => (
                                <div
                                    key={employee.id}
                                    className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                                >
                                    <div className="p-5 text-left space-y-1">
                                        <h3 className="font-bold text-xl text-gray-800 font-display">
                                            {employee.company_name}
                                        </h3>
                                        <p className="text-gray-600 flex gap-2 font-semibold">
                                            ID:
                                            <span className="font-normal">{employee.id}</span>{" "}
                                        </p>
                                        <p className="text-gray-600 flex gap-2 font-semibold">
                                            Registration Date:
                                            <span className="font-normal">{getJoinedTimeDisplay(employee)}</span>
                                        </p>
                                        <p className="text-gray-600 flex gap-2 font-semibold">
                                            District:
                                            <span className="font-normal">{employee.company_district}</span>
                                        </p>
                                        <p className="text-gray-600 flex gap-2 font-semibold">
                                            Email:
                                            <span className="font-normal">{employee.email}</span>
                                        </p>
                                        <p className="text-gray-600 flex gap-2 font-semibold">
                                            Mobile Number:
                                            <span className="font-normal">{employee.mobile_number}</span>
                                        </p>
                                        {passwordAccess && (
                                            <p className="text-gray-600 flex gap-2 font-semibold">
                                                Password:
                                                <span className="font-normal">{employee.password}</span>
                                            </p>
                                        )}

                                        {/* Actions */}
                                        <div className="mt-4 pt-3 border-t border-gray-200 flex flex-wrap gap-2 justify-around">
                                            {isBlocked(employee.id, employee.mobile_number) ? (
                                                <button
                                                    onClick={() =>
                                                        handleUnblockProfile(employee.id, employee.mobile_number)
                                                    }
                                                    className="text-green-600 hover:text-green-800 font-medium text-sm"
                                                >
                                                    Unblock
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() =>
                                                        handleBlockProfile(employee.id, employee.mobile_number, "employee")
                                                    }
                                                    className="text-red-600 hover:text-red-800 font-medium text-sm"
                                                >
                                                    Block
                                                </button>
                                            )}

                                            {customerType === "mainAdmin" && (
                                                <button
                                                    onClick={() => navigate(`/change-password/${employee.id}`)}
                                                    className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                                                >
                                                    Change Password
                                                </button>
                                            )}

                                            <button
                                                onClick={() => openDeleteModal(employee.id)}
                                                className="text-red-600 hover:text-red-800 font-medium text-sm"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
                        <h3 className="text-2xl font-bold text-red-600 mb-3">Delete Employer</h3>
                        <p className="text-gray-700 mb-5">
                            This will <strong>permanently delete</strong> the employer and{" "}
                            <strong>all associated job posts</strong>. This action cannot be undone.
                        </p>

                        <label className="block text-sm font-semibold text-gray-700 mb-2">Admin Password Required</label>
                        <input
                            type="password"
                            value={deletePassword}
                            onChange={(e) => {
                                setDeletePassword(e.target.value);
                                setPasswordError("");
                            }}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 mb-3"
                            placeholder="Enter admin password"
                            autoFocus
                        />

                        {passwordError && <p className="text-red-500 text-sm mb-4 animate-pulse">{passwordError}</p>}

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={closeDeleteModal}
                                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeleteWithPassword}
                                className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition"
                            >
                                Delete Permanently
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default DeleteProfiles;
